/**
 * The auto-refresh control: cadence, staleness, and a manual refresh.
 *
 * The age readout is the point of the component. Without it, an auto-updating
 * dashboard is *less* trustworthy than a static one — a figure that might be
 * ten seconds or ten minutes old looks identical, and the user has no way to
 * tell which. Saying when we last asked is what makes the automation safe.
 *
 * Note what it does and does not claim: "updated 12s ago" is when the *fetch*
 * happened, not when the number was computed. The pricing and rollup workers
 * run on much slower cycles, so a freshly-fetched figure can still be minutes
 * old upstream. The label says "Updated" rather than "Live" for that reason.
 */

import { useEffect, useState } from 'react'
import { describeAge, INTERVALS } from './useAutoRefresh'

interface Props {
  readonly intervalMs: number
  readonly onIntervalChange: (ms: number) => void
  readonly lastRefreshAt: number
  readonly paused: boolean
  readonly onRefreshNow: () => void
}

export function AutoRefreshControl(props: Props) {
  // Re-render on a slow clock so the age readout advances between refreshes.
  // 1s would be a lie at this precision (see `describeAge`); 5s is enough to
  // keep "just now" from lingering after it stops being true.
  const [, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(timer)
  }, [])

  const age = describeAge(Date.now() - props.lastRefreshAt)

  return (
    <div className="auto-refresh">
      <label>
        <span className="auto-refresh__label">Auto-update</span>
        <select
          value={String(props.intervalMs)}
          onChange={(e) => props.onIntervalChange(Number(e.target.value))}
        >
          {INTERVALS.map((option) => (
            <option key={option.ms} value={String(option.ms)}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <button type="button" onClick={props.onRefreshNow} title="Refresh now">
        Refresh
      </button>

      <span className="auto-refresh__age" aria-live="polite">
        {props.paused ? 'Paused (tab hidden)' : `Updated ${age}`}
      </span>
    </div>
  )
}
